import { useEffect, useState } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import { LogOut } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Logo } from "@/components/Logo";
import { EnquiriesTab } from "./EnquiriesTab";
import { ProjectsTab } from "./ProjectsTab";
import { FaqsTab } from "./FaqsTab";
import { TestimonialsTab } from "./TestimonialsTab";
import { api } from "@/lib/api";

export default function AdminDashboard() {
  const location = useLocation();
  const navigate = useNavigate();
  const [user, setUser] = useState(location.state?.user || null);
  const [checking, setChecking] = useState(!location.state?.user);

  useEffect(() => {
    if (location.state?.user) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/auth/me");
        if (!cancelled) setUser(data);
      } catch {
        try {
          await api.post("/auth/refresh");
          const { data } = await api.get("/auth/me");
          if (!cancelled) setUser(data);
        } catch {
          if (!cancelled) { navigate("/admin/login", { replace: true }); return; }
        }
      } finally {
        if (!cancelled) setChecking(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const logout = async () => {
    await api.post("/auth/logout").catch(() => {});
    navigate("/admin/login", { replace: true });
  };

  if (checking) {
    return <div className="flex min-h-screen items-center justify-center bg-offwhite"><p className="text-sm text-charcoal/50">Loading…</p></div>;
  }

  return (
    <div className="min-h-screen bg-offwhite" data-testid="admin-dashboard">
      <header className="bg-charcoal">
        <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-5 md:px-8">
          <Link to="/" aria-label="Back to website"><Logo /></Link>
          <div className="flex items-center gap-4">
            <span className="hidden text-xs text-white/50 sm:block">{user?.email}</span>
            <button type="button" onClick={logout} data-testid="admin-logout-button"
              className="inline-flex items-center gap-2 rounded-full border border-white/15 px-4 py-2 text-xs font-bold text-white/70 transition-colors hover:border-gold hover:text-gold">
              <LogOut className="h-3.5 w-3.5" /> Logout
            </button>
          </div>
        </div>
      </header>
      <main className="mx-auto max-w-7xl px-5 py-10 md:px-8">
        <h1 className="font-display text-3xl text-charcoal">Content &amp; Enquiries</h1>
        <Tabs defaultValue="enquiries" className="mt-8">
          <TabsList data-testid="admin-tabs" className="bg-charcoal/5">
            <TabsTrigger value="enquiries" data-testid="tab-enquiries">Enquiries</TabsTrigger>
            <TabsTrigger value="projects" data-testid="tab-projects">Projects</TabsTrigger>
            <TabsTrigger value="faqs" data-testid="tab-faqs">FAQs</TabsTrigger>
            <TabsTrigger value="testimonials" data-testid="tab-testimonials">Testimonials</TabsTrigger>
          </TabsList>
          <TabsContent value="enquiries" className="mt-6"><EnquiriesTab /></TabsContent>
          <TabsContent value="projects" className="mt-6"><ProjectsTab /></TabsContent>
          <TabsContent value="faqs" className="mt-6"><FaqsTab /></TabsContent>
          <TabsContent value="testimonials" className="mt-6"><TestimonialsTab /></TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
